/**
 * v2's buttons. Four kinds, one meaning each, used the same way on every screen:
 *
 *   primary    the action this screen exists for - filled in the discipline's colour
 *   secondary  a real action, but not the main one - tinted in the same colour
 *   quiet      navigation and housekeeping - white, outlined
 *   danger     anything that removes work - red
 *
 * The colour comes from the nearest data-discipline (styles/globals.css), so the same
 * primary button is ledger blue in IAS accounting and evergreen in Baseline Accounting.
 */

import type { ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger';
export type ButtonSize = 'sm' | 'md';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-600 text-white shadow-sm hover:bg-accent-700 focus-visible:outline-accent-600',
  secondary:
    'border border-accent-200 bg-accent-50 text-accent-700 hover:border-accent-300 hover:bg-accent-100 focus-visible:outline-accent-600',
  quiet:
    'border border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 focus-visible:outline-gray-500',
  danger:
    'border border-red-200 bg-white text-red-700 hover:border-red-300 hover:bg-red-50 focus-visible:outline-red-600',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'gap-1.5 rounded-md px-2.5 py-1 text-xs',
  md: 'gap-2 rounded-lg px-3.5 py-2 text-sm',
};

/** The classes, for a <label> wrapping a file input that has to look like a button. */
export function buttonClass(variant: ButtonVariant = 'quiet', size: ButtonSize = 'md'): string {
  return clsx(
    'inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-45',
    VARIANTS[variant],
    SIZES[size],
  );
}

export function Button({
  variant = 'quiet',
  size = 'md',
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return <button type={type} className={clsx(buttonClass(variant, size), className)} {...rest} />;
}
